import React, { useEffect, useState } from 'react';
import { Button } from '../ui';
import { useToast } from '../../hooks/useToast';
import { apiClient } from '../../lib/api';
import { portalApi } from '../../portal/api/client';
import type { PortalEvent } from '../../portal/api/types';

interface ProjectEventLinkProps {
  projectId: string;
  eventId: string | null;
  disabled?: boolean;
  /** Fires with the new link (or null after an unlink) once the project is saved. */
  onLinked: (eventId: string | null) => void;
}

const MAX_MATCHES = 6;

const eventLabel = (e: PortalEvent): string =>
  [e.name, e.dateStart?.slice(0, 4), e.city].filter(Boolean).join(' · ');

/** Shows the conference a project points at and lets the operator pick one from the catalog. */
export const ProjectEventLink: React.FC<ProjectEventLinkProps> = ({
  projectId,
  eventId,
  disabled,
  onLinked,
}) => {
  const { showToast } = useToast();
  const [linked, setLinked] = useState<PortalEvent | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [matches, setMatches] = useState<PortalEvent[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    if (!eventId) {
      setLinked(null);
      return;
    }
    portalApi
      .getEvent(eventId)
      .then((e) => {
        if (live) setLinked(e);
      })
      .catch((err) => {
        // Non-blocking: the id below still tells the operator a link exists.
        console.error('linked event load failed:', err);
        if (live) setLinked(null);
      });
    return () => {
      live = false;
    };
  }, [eventId]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!debounced) {
      setMatches([]);
      return;
    }
    let live = true;
    setSearching(true);
    portalApi
      .getEvents(debounced, 'name', 1)
      .then((res) => {
        if (live) setMatches(res.items.slice(0, MAX_MATCHES));
      })
      .catch((err) => {
        if (!live) return;
        console.error('conference search failed:', err);
        showToast('Could not search the conference catalog', 'error');
      })
      .finally(() => {
        if (live) setSearching(false);
      });
    return () => {
      live = false;
    };
  }, [debounced, showToast]);

  const save = async (next: string | null) => {
    setSaving(true);
    try {
      const updated = await apiClient.setPipelineEvent(projectId, next);
      setQuery('');
      setMatches([]);
      onLinked(updated.event_id ?? null);
      showToast(next ? 'Conference linked' : 'Conference unlinked', 'success');
    } catch (err) {
      console.error('conference link failed:', err);
      showToast(err instanceof Error ? err.message : 'Could not update the conference', 'error');
    } finally {
      setSaving(false);
    }
  };

  const busy = Boolean(disabled) || saving;
  const inputClass =
    'flex-1 min-w-56 text-xs border border-neutral-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 disabled:opacity-50';

  return (
    <div>
      <h4 className="text-13 font-semibold text-neutral-700 mb-1">Conference</h4>
      {eventId && (
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm text-neutral-800">
            {linked ? eventLabel(linked) : `Linked conference ${eventId}`}
          </span>
          <Button variant="secondary" size="sm" onClick={() => void save(null)} disabled={busy}>
            Unlink
          </Button>
        </div>
      )}
      <input
        id="event-search"
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={eventId ? 'Link a different conference' : 'Search the conference catalog by name'}
        aria-label="Search the conference catalog"
        disabled={busy}
        className={inputClass}
      />
      {matches.length > 0 && (
        <ul
          className="mt-1 border border-neutral-200 rounded divide-y divide-neutral-100 max-h-40 overflow-auto"
          aria-label="Matching conferences"
        >
          {matches.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => void save(e.id)}
                disabled={busy}
                className="w-full text-left text-xs px-2 py-1.5 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 disabled:opacity-50"
              >
                {eventLabel(e)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {debounced && !searching && matches.length === 0 && (
        <p className="text-xs text-neutral-500 mt-1">No conferences match.</p>
      )}
    </div>
  );
};
